import { Users, PhoneOutgoing, UserCheck, Clock, Mail, Shield, AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { presenceStateOf, type Operator, type PresenceState } from '../../stores/presenceStore'

/**
 * オペレーター一覧
 * バッジはサーバ導出の presenceState (受話可能性) を表示する:
 *  - available: 受付中 / busy: 通話中 / away: 離席 (着信音ミュート)
 *  - impaired: 要対処 (Device 未登録・マイク拒否 = 見た目オンラインでも受話不能)
 */

const STATE_BADGE: Record<PresenceState, { label: string; badgeClass: string; dotClass: string }> = {
  available: {
    label: '受付中',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    dotClass: 'bg-green-500 animate-pulse',
  },
  busy: {
    label: '通話中',
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
    dotClass: 'bg-orange-500',
  },
  away: {
    label: '離席',
    badgeClass: 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300',
    dotClass: 'bg-gray-400',
  },
  impaired: {
    label: '要対処',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
    dotClass: 'bg-red-500 animate-pulse',
  },
}

interface CompactOperatorListProps {
  operators: Operator[]
  currentUserId?: string
  onCallOperator: (userId: string, displayName?: string) => void
  callingOperatorId: string | null
  isCallDisabled: boolean
}

export default function CompactOperatorList({
  operators,
  currentUserId,
  onCallOperator,
  callingOperatorId,
  isCallDisabled
}: CompactOperatorListProps) {
  const [showAll, setShowAll] = useState(false)

  // 統計情報を計算 (presenceState ベース)
  const onlineCount = operators.length
  const availableCount = operators.filter(op => presenceStateOf(op) === 'available').length
  const busyCount = operators.filter(op => presenceStateOf(op) === 'busy').length
  const impairedCount = operators.filter(op => presenceStateOf(op) === 'impaired').length

  // 表示するオペレーター（最大8名）
  const displayedOperators = showAll ? operators : operators.slice(0, 8)
  const remainingCount = operators.length - 8

  if (operators.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <Users className="w-5 h-5" />
          オンライン
        </h2>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          現在オンラインのオペレーターはいません
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-6 h-6" />
            オンラインオペレーター
          </h2>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-semibold">
              {onlineCount}人
            </span>
          </div>
        </div>

        {/* 統計サマリー: 受話可能性ベース */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/30 rounded-lg p-3 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-white" />
              </div>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">受付中</span>
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">{availableCount}</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/30 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300">通話中</span>
            </div>
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{busyCount}</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-900/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              <span className="text-xs font-medium text-red-700 dark:text-red-300">要対処</span>
            </div>
            <p className="text-2xl font-bold text-red-900 dark:text-red-100">{impairedCount}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayedOperators.map((op) => {
            const badge = STATE_BADGE[presenceStateOf(op)]
            return (
            <div
              key={op.userId}
              className="relative bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-200"
            >
              {/* ステータスインジケーター */}
              <div className="absolute top-3 right-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.badgeClass}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${badge.dotClass}`}></span>
                  {badge.label}
                </span>
              </div>

              {/* アバター - 大きく表示 */}
              <div className="flex flex-col items-center mb-3">
                {op.avatar ? (
                  <img
                    src={op.avatar}
                    alt={op.displayName || op.name || op.email || 'avatar'}
                    className="w-20 h-20 rounded-full object-cover border-4 border-white dark:border-gray-600 shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg border-4 border-white dark:border-gray-600">
                    {(op.displayName || op.name || op.email || '?')[0].toUpperCase()}
                  </div>
                )}
              </div>

              {/* ユーザー情報 */}
              <div className="text-center mb-3">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1 truncate">
                  {op.displayName || op.name || op.email || '不明なユーザー'}
                </h3>

                {op.email && (
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-2">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{op.email}</span>
                  </div>
                )}

                {op.role === 'admin' && (
                  <div className="flex items-center justify-center gap-1 px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium inline-flex">
                    <Shield className="w-3 h-3" />
                    管理者
                  </div>
                )}
              </div>

              {/* 内線発信ボタン */}
              {op.userId !== currentUserId && (
                <button
                  onClick={() => onCallOperator(op.userId, op.displayName || op.name || op.email)}
                  disabled={callingOperatorId === op.userId || isCallDisabled}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                  title="内線発信"
                >
                  <PhoneOutgoing className="w-4 h-4" />
                  {callingOperatorId === op.userId ? '発信中...' : '内線発信'}
                </button>
              )}

              {op.userId === currentUserId && (
                <div className="w-full px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm font-medium text-center">
                  あなた
                </div>
              )}
            </div>
            )
          })}
        </div>

        {/* 「他○名」表示と展開ボタン */}
        {remainingCount > 0 && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="w-full mt-4 py-3 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold transition-colors rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            他{remainingCount}名を表示
          </button>
        )}

        {showAll && operators.length > 8 && (
          <button
            onClick={() => setShowAll(false)}
            className="w-full mt-4 py-3 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-semibold transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            折りたたむ
          </button>
        )}
      </div>
    </div>
  )
}
